import { fromEvent, merge } from 'rxjs'
import { filter, map, first } from 'rxjs/operators'
import { transitionEnd } from '../animation'

import hoverLabel from '../form/hover-label'

export const modalController = {
    modalContainer: null,
    modals: {},
    buttons: {},
    currentOpenModal: null,
    template: null,
    lastFocus: null,
    iconPath: '',
    openModal,
    closeModal,
    addModal
}

export function setupModals() {
    const container = document.querySelector('.js-modal-container')
    if (!container) {
        return
    }

    const buttonEls = document.querySelectorAll('button[data-modal-id]')
    const buttons = Array.from(buttonEls)
    modalController.buttons = buttons.reduce((acc, btn) => {
        btn.setAttribute('aria-haspopup', 'dialog')
        btn.setAttribute('aria-expanded', 'false')

        const id = btn.dataset.modalId
        if (!acc[id]) {
            acc[id] = []
        }

        acc[id].push(btn)
        return acc
    }, {})

    modalController.iconPath = container.dataset.iconPath || '/images/svgicons.svg'

    modalController.openModal = modalController.openModal.bind(modalController)
    modalController.closeModal = modalController.closeModal.bind(modalController)
    modalController.addModal = modalController.addModal.bind(modalController)

    modalController.template = document.querySelector('.js-modal-template')
    if (!modalController.template) {
        return
    }
    modalController.template = modalController.template.innerHTML

    modalController.modalContainer = container
    let modals = modalController.modalContainer.querySelectorAll('[data-modal-item-id]')
    modals = Array.from(modals)
    modals.forEach(el => {
        const id = el.dataset.modalItemId
        modalController.addModal({ id, content: el })
    })

    if (typeof window.modals !== 'undefined') {
        window.modals.forEach(el => {
            if (el.content) {
                modalController.addModal(el)
            }
            if (el.open) {
                modalController.openModal(el.id)
            }
        })
    }

    setupEvents()
}

function setupEvents() {
    fromEvent(document, 'click')
        .pipe(
            filter(el => !!el.target.dataset.modalId),
            map(el => el.target.dataset.modalId)
        )
        .subscribe(modalController.openModal)

    merge(
        fromEvent(document, 'click').pipe(
            filter(el => el.target.classList.contains('js-modal-close'))
        )
    )

    fromEvent(modalController.modalContainer, 'click').subscribe(modalController.closeModal)

    const keyDown = fromEvent(modalController.modalContainer, 'keydown')

    keyDown
        .pipe(filter(evt => evt.key === 'Tab' && modalController.currentOpenModal))
        .subscribe(evt => {
            const { focusEls } = modalController.currentOpenModal
            if (focusEls.length === 1) {
                evt.preventDefault()
                return
            }

            const currentActive = document.activeElement
            const firstFocusable = focusEls[0]
            const lastFocusable = focusEls[focusEls.length - 1]

            if (evt.shiftKey) {
                if (currentActive === firstFocusable) {
                    evt.preventDefault()
                    lastFocusable.focus()
                }
            } else {
                if (currentActive === lastFocusable) {
                    evt.preventDefault()
                    firstFocusable.focus()
                }
            }
        })

    keyDown
        .pipe(filter(evt => evt.key === 'Escape' && modalController.currentOpenModal))
        .subscribe(modalController.closeModal)
}

function openModal(modalID) {
    if (!this.modals[modalID]) {
        console.warn('No Modal find with id: ' + modalID)
        return
    }

    this.lastFocus = document.activeElement
    const { element, firstFocusElements } = this.modals[modalID]

    this.modalContainer.hidden = false
    element.hidden = false
    document.body.classList.add('h-lock')

    if (this.buttons[modalID] && this.buttons[modalID].length) {
        this.buttons[modalID].forEach(btn => {
            btn.setAttribute('aria-expanded', 'true')
        })
    }

    requestAnimationFrame(() => {
        if (!this.modalContainer.classList.contains('is-active')) {
            this.modalContainer.classList.add('is-active')
        }

        if (this.currentOpenModal) {
            if (this.currentOpenModal.element === element) {
                return
            } else {
                this.currentOpenModal.element.classList.remove('is-active')
                transitionEnd(this.currentOpenModal.element)
                    .pipe(first())
                    .subscribe(() => {
                        this.currentOpenModal.element.hidden = true
                    })
            }
        }

        this.currentOpenModal = this.modals[modalID]
        element.classList.add('is-active')
        firstFocusElements.focus()
    })
}

function closeModal() {
    const { element, id } = this.currentOpenModal

    this.modalContainer.classList.remove('is-active')

    if (this.buttons[id] && this.buttons[id].length) {
        this.buttons[id].forEach(btn => {
            btn.setAttribute('aria-expanded', 'false')
        })
    }

    element.classList.remove('is-active')
    this.currentOpenModal = null
    transitionEnd(element)
        .pipe(first())
        .subscribe(() => {
            this.modalContainer.hidden = true
            element.hidden = true
        })

    this.lastFocus.focus()
    document.body.classList.remove('h-lock')
}

function addModal({ id, content, header = '', icon = '' }) {
    if (modalController.modals[id]) {
        return
    }

    let element
    if (typeof content === 'string') {
        element = createModal(id, content, header, icon)
    } else {
        element = content
    }

    let focusableElements = element.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
    )
    focusableElements = Array.from(focusableElements)

    modalController.modals[id] = {
        id,
        element,
        focusEls: focusableElements,
        firstFocusElements: focusableElements[0]
    }

    fromEvent(element, 'click')
        .pipe(
            filter(evt => {
                return evt.target.classList.contains('js-modal-close')
            })
        )
        .subscribe(modalController.closeModal)

    const hasInputGroup = element.querySelector('.js-hover-label')
    hasInputGroup && hoverLabel()

    element.addEventListener('click', evt => {
        evt.stopPropagation()
    })

    if (element.classList.contains('is-active')) {
        modalController.currentOpenModal = modalController.modals[id]
    }
}

function createModal(id, content, header, icon) {
    let iconHtml = ''
    if (icon) {
        iconHtml = `<svg><use xlink:href="${modalController.iconPath}#${icon}" /></svg>`
    }

    const html = modalController.template
        .replace(/{id}/g, id)
        .replace(/{header}/g, header)
        .replace(/{content}/g, content)
        .replace(/{icon}/g, iconHtml)
    const div = document.createElement('div')
    div.innerHTML = html
    modalController.modalContainer.appendChild(div.firstElementChild)
    return modalController.modalContainer.querySelector('[data-modal-item-id=' + id + ']')
}
